import { differenceInMilliseconds } from 'date-fns';
import { CombinedRouteLegType, RouteChildNodeDataType, RouteNodeType } from '@lp/features/RoutePlanner/types';
import {
  SegmentTypeEnum,
  AviaLegTypes,
  GroundLegTypes,
  AirportLegLocationReference,
  FacilityLegLocationReference,
  CustomAddressLegLocationReference,
} from '@/api/apis/route';
import { RouteSegmentType } from '@/types/route';
import { AVIA_LEG_TYPES, getSegmentMapKey, isAviaLegType, isGroundLegType } from '@/utils/route';

export const combineSimilarTypeLegs = <T extends AviaLegTypes | GroundLegTypes>(
  legs: T[],
): CombinedRouteLegType<T> | null => {
  if (!legs.length) return null;

  return legs.reduce<CombinedRouteLegType<T>>((acc, leg, currentIndex) => {
    const legDuration = differenceInMilliseconds(new Date(leg.arrivalTime), new Date(leg.departureTime));

    if (!currentIndex) {
      return {
        ...leg,
        segmentDurationInMs: legDuration,
      };
    }

    return {
      ...acc,
      resources: leg.resources,
      arrivalTime: leg.arrivalTime,
      arrivalLocalTimezone: leg.arrivalLocalTimezone,
      arrivalLocation: leg.arrivalLocation,
      segmentDurationInMs: (acc.segmentDurationInMs || 0) + legDuration,
    };
  }, {} as CombinedRouteLegType<T>);
};

const splitLegsToGroups = (legs: (AviaLegTypes | GroundLegTypes)[]): (AviaLegTypes | GroundLegTypes)[][] => {
  let currentListIndex = 0;
  const groupMap = new Map<number, (AviaLegTypes | GroundLegTypes)[]>();

  legs.forEach((leg, currentIndex) => {
    if (!currentIndex) {
      groupMap.set(currentListIndex, [leg]);
    } else {
      const currentLegsList = groupMap.get(currentListIndex)!;
      const lastLegInList = currentLegsList.at(-1)!;
      const isSameLocations = leg.departureLocation.locationId === lastLegInList.arrivalLocation.locationId;

      if (isSameLocations) {
        groupMap.set(currentListIndex, [...currentLegsList, leg]);
      } else {
        currentListIndex += 1;
        groupMap.set(currentListIndex, [leg]);
      }
    }
  });

  return Array.from(groupMap.values());
};

const getLegLocation = (
  leg: AviaLegTypes | GroundLegTypes,
  locationType: 'departureLocation' | 'arrivalLocation',
): AirportLegLocationReference | FacilityLegLocationReference | CustomAddressLegLocationReference => {
  const isGroundLeg = isGroundLegType(leg);
  return isGroundLeg ? leg[locationType].locationReference || leg[locationType].airportReference : leg[locationType];
};

type SegmentWithLegs = {
  segment: RouteSegmentType;
  segmentLegs: (AviaLegTypes | GroundLegTypes)[];
};

const getSegmentsFromLegList = (
  segment: RouteSegmentType,
  legs: Map<string, (AviaLegTypes | GroundLegTypes)[]>,
): SegmentWithLegs[] => {
  const segmentLegs = legs.get(getSegmentMapKey(segment)) || [];
  const isCustomSegments = [SegmentTypeEnum.reposition, SegmentTypeEnum.returnToBase, SegmentTypeEnum.custom].includes(
    segment.type,
  );

  if (!segmentLegs.length || isCustomSegments) {
    return [{ segment, segmentLegs }];
  }

  const groupOfLegs = splitLegsToGroups(segmentLegs);

  return groupOfLegs.reduce((acc, group, currentIndex) => {
    const accumulator = [...acc];
    const prevGroup = currentIndex ? groupOfLegs.at(currentIndex - 1) : null;
    const firstLeg = group.at(0)!;
    const lastLeg = group.at(-1)!;
    const isSameStartLocation = firstLeg.departureLocation.locationId === segment.startLocation.locationId;
    const isSameEndLocation = lastLeg.arrivalLocation.locationId === segment.endLocation.locationId;

    if (isSameStartLocation && isSameEndLocation) {
      accumulator.push({
        segment,
        segmentLegs: group,
      } as SegmentWithLegs);

      return accumulator;
    }

    const firstLegLocation = getLegLocation(firstLeg, 'departureLocation');
    const lastLegLocation = getLegLocation(lastLeg, 'arrivalLocation');

    if (isSameStartLocation) {
      const newSegment = {
        id: `${segment.id}-${currentIndex}`,
        type: segment.type,
        repositionType: segment.repositionType,
        startLocation: segment.startLocation,
        endLocation: lastLegLocation,
      };

      accumulator.push({
        segment: newSegment,
        segmentLegs: group,
      });
    }

    if (!currentIndex && !isSameStartLocation) {
      const newSegment = {
        id: `${segment.id}-empty-start`,
        type: segment.type,
        repositionType: segment.repositionType,
        startLocation: segment.startLocation,
        endLocation: firstLegLocation,
      };

      accumulator.push({
        segment: newSegment,
        segmentLegs: [],
      });
    }

    if (prevGroup?.length) {
      const lastLegInPrevGroup = prevGroup.at(-1)!;
      const lastLegInPrevGroupLocation = getLegLocation(lastLegInPrevGroup, 'arrivalLocation');

      const newSegment = {
        id: `${segment.id}-${currentIndex}-empty`,
        type: segment.type,
        repositionType: segment.repositionType,
        startLocation: lastLegInPrevGroupLocation,
        endLocation: firstLegLocation,
      };

      accumulator.push({
        segment: newSegment,
        segmentLegs: [],
      });
    }

    if (!isSameStartLocation && !isSameEndLocation) {
      const newSegment = {
        id: `${segment.id}-${currentIndex}`,
        type: segment.type,
        repositionType: segment.repositionType,
        startLocation: firstLegLocation,
        endLocation: lastLegLocation,
      };

      accumulator.push({
        segment: newSegment,
        segmentLegs: group,
      });
    }

    if (isSameEndLocation) {
      const newSegment = {
        id: `${segment.id}-${currentIndex}`,
        type: segment.type,
        repositionType: segment.repositionType,
        startLocation: firstLegLocation,
        endLocation: segment.endLocation,
      };

      accumulator.push({
        segment: newSegment,
        segmentLegs: group,
      });
    }

    if (currentIndex === groupOfLegs.length - 1 && !isSameEndLocation) {
      const newSegment = {
        id: `${segment.id}-empty-end`,
        type: segment.type,
        repositionType: segment.repositionType,
        startLocation: lastLegLocation,
        endLocation: segment.endLocation,
      };

      accumulator.push({
        segment: newSegment,
        segmentLegs: [],
      });
    }

    return accumulator;
  }, [] as SegmentWithLegs[]);
};

export const normalizeRouteData = (
  segments: RouteSegmentType[],
  legs: Map<string, (AviaLegTypes | GroundLegTypes)[]>,
): { segments: RouteSegmentType[]; legs: Map<string, (AviaLegTypes | GroundLegTypes)[]> } => {
  const newLegs = new Map<string, (AviaLegTypes | GroundLegTypes)[]>();
  const splitSegments: RouteSegmentType[] = [];

  const segmentsList = segments.flatMap((segment) => {
    return getSegmentsFromLegList(segment, legs);
  });

  segmentsList.forEach(({ segment, segmentLegs }) => {
    const hasLegs = !!segmentLegs.length;

    const airLegs = segmentLegs.filter((l): l is AviaLegTypes => AVIA_LEG_TYPES.includes(l.legType));

    const isCustomSegments = [
      SegmentTypeEnum.reposition,
      SegmentTypeEnum.returnToBase,
      SegmentTypeEnum.custom,
    ].includes(segment.type);
    const isFullNodeSegment = airLegs.length <= 1;
    const segmentWithoutChanges = isCustomSegments || !hasLegs || isFullNodeSegment;

    if (segmentWithoutChanges) {
      newLegs.set(getSegmentMapKey(segment), segmentLegs);
      splitSegments.push(segment);
      return;
    }

    airLegs.forEach((l, index) => {
      const startLocation = !index ? segment.startLocation : l.departureLocation;
      const endLocation = airLegs.length - 1 === index ? segment.endLocation : l.arrivalLocation;
      const currentLegs = segmentLegs.filter(
        (cl) =>
          cl.departureLocation.locationId === startLocation.locationId ||
          cl.arrivalLocation.locationId === endLocation.locationId,
      );
      const createdSegment = {
        id: `${segment.id}-middle-${index}`,
        type: segment.type,
        repositionType: segment.repositionType,
        startLocation,
        endLocation,
      };

      newLegs.set(getSegmentMapKey(createdSegment), currentLegs);
      splitSegments.push(createdSegment);
    });
  });

  return { segments: splitSegments, legs: newLegs };
};

export const getSegmentLeg = (
  segmentsById: Map<string, RouteSegmentType>,
  legs: Map<string, (AviaLegTypes | GroundLegTypes)[]>,
  node: RouteNodeType | RouteNodeType<RouteChildNodeDataType>,
): CombinedRouteLegType<AviaLegTypes | GroundLegTypes> | null => {
  const segment = segmentsById.get(node.data.segmentId!)!;
  const segmentLegs = legs.get(getSegmentMapKey(segment)) || [];
  const legsWithAir = segmentLegs.filter((l): l is AviaLegTypes => isAviaLegType(l));
  const currentSegmentLegs = legsWithAir.length ? legsWithAir : segmentLegs;
  return combineSimilarTypeLegs(currentSegmentLegs);
};
